import { useState, useRef, useEffect } from 'react';
import { X, Paperclip, Search } from 'lucide-react';
import type { TaskStatus, Priority, Attachment } from '../../types';
import { useApp } from '../../context/AppContext';
import { createObjectURL } from '../../utils/helpers';
import RichTextEditor from '../common/RichTextEditor';
import Avatar from '../common/Avatar';

interface NewTaskFormProps {
  boardId: string;
  initialStatus: TaskStatus;
  onClose: () => void;
}

export default function NewTaskForm({ boardId, initialStatus, onClose }: NewTaskFormProps) {
  const { state, apiCreateTask } = useApp();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [attachments, setAttachments] = useState<{ name: string; size: number; type: string; url: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const assigneeContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (assigneeContainerRef.current && !assigneeContainerRef.current.contains(e.target as Node)) {
        setShowAssigneeDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredUsers = state.users.filter(
    (user) =>
      !assigneeIds.includes(user.id) &&
      user.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const handleAddAssignee = (userId: string) => {
    setAssigneeIds((prev) => [...prev, userId]);
    setAssigneeSearch('');
    setShowAssigneeDropdown(false);
  };

  const handleRemoveAssignee = (userId: string) => {
    setAssigneeIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newAttachments = Array.from(files).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: createObjectURL(file),
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleLabel = (labelId: string) => {
    setSelectedLabelIds((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || submitting) return;

    setSubmitting(true);
    const created = await apiCreateTask({
      title: title.trim(),
      description: description.trim(),
      status: initialStatus,
      priority,
      assigneeIds,
      reporterId: state.currentUser.id,
      boardId,
      sprintId: null,
      labelIds: selectedLabelIds,
      comments: [],
      attachments: attachments as Attachment[],
      subtasks: [],
      storyPoints: 0,
      dueDate: dueDate || null,
    });
    setSubmitting(false);

    if (created) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-fade-in">
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Nova Tarefa</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="task-title" className="block text-sm font-medium text-slate-700 mb-1">
              Titulo
            </label>
            <input
              id="task-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Digite o titulo da tarefa..."
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descricao
            </label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Digite a descricao da tarefa..."
              minHeight="100px"
            />
          </div>

          <div>
            <label htmlFor="task-priority" className="block text-sm font-medium text-slate-700 mb-1">
              Prioridade
            </label>
            <select
              id="task-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as Priority)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all bg-white"
            >
              <option value="low">Baixa</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
              <option value="critical">Critica</option>
            </select>
          </div>

          <div ref={assigneeContainerRef}>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Responsavel
            </label>
            {assigneeIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {assigneeIds.map((id) => {
                  const user = state.users.find((u) => u.id === id);
                  if (!user) return null;
                  return (
                    <span
                      key={user.id}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-100 text-sm text-slate-700"
                    >
                      <Avatar name={user.name} color={user.avatarColor} size="sm" />
                      <span className="text-xs font-medium">{user.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAssignee(user.id)}
                        className="p-0.5 rounded text-slate-400 hover:text-red-500 transition-colors"
                        aria-label={`Remover ${user.name}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="relative">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={assigneeSearch}
                  onChange={(e) => {
                    setAssigneeSearch(e.target.value);
                    setShowAssigneeDropdown(true);
                  }}
                  onFocus={() => setShowAssigneeDropdown(true)}
                  placeholder="Buscar responsavel..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all"
                />
              </div>
              {showAssigneeDropdown && filteredUsers.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleAddAssignee(user.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors first:rounded-t-xl last:rounded-b-xl"
                    >
                      <Avatar name={user.name} color={user.avatarColor} size="sm" />
                      <span>{user.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="task-due-date" className="block text-sm font-medium text-slate-700 mb-1">
              Prazo
            </label>
            <input
              id="task-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all bg-white"
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-slate-700 mb-1">Etiquetas</span>
            <div className="flex flex-wrap gap-2">
              {state.labels.map((label) => (
                <button
                  key={label.id}
                  type="button"
                  onClick={() => toggleLabel(label.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${selectedLabelIds.includes(label.id)
                    ? 'ring-2 ring-brand-300 border-brand-400'
                    : 'border-slate-200 hover:border-slate-300'
                    }`}
                  style={{
                    backgroundColor: `${label.color}20`,
                    color: label.color,
                  }}
                >
                  {label.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Anexos</label>
            <label
              htmlFor="task-files"
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 hover:border-brand-400 hover:text-brand-500 cursor-pointer transition-all"
            >
              <Paperclip size={16} />
              <span>Adicionar arquivos...</span>
              <input
                id="task-files"
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((att, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-slate-50 text-sm"
                  >
                    <span className="text-slate-600 truncate">{att.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(index)}
                      className="p-0.5 rounded text-slate-400 hover:text-red-500 transition-colors"
                      aria-label="Remover anexo"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!title.trim() || submitting}
            className="flex-1 px-4 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Criando...' : 'Criar Tarefa'}
          </button>
        </div>
      </form>
    </div>
  );
}
